import { defineStore } from 'pinia'
import api from '@/axios/api'

import { AppCookies } from '@models/classes/cookies'
import { AxiosErrorHandler } from '@models/classes/axiosErrorHadler'

import { LanguageEnum } from '@models/entities/languages'
import { type CredentialType } from '@models/entities/credentials'
import { type PointType } from '@models/entities/points'
import { type TransactionType } from '@models/entities/transactions'
import { type UserCompanyType, type UserType } from '@models/entities/users'
import { type SalePointPermissionsType, type UserPermissionsType } from '@models/entities/permissions'
import {
  type GetQrCodePayloadDataType,
  type InviteUserRequestPayloadDataType,
  type LoginRequestPayloadDataType,
  type LoginMFARequestPayloadDataType,
  type PostUserInfoRequestPayloadDataType,
  type PostUserPermissionsRequestPayloadDataType,
  type CreateInvoicePayloadDataType,
  type SetupMFAPayloadDataType,
  type VerifyMFAPayloadDataType,
  type DisableMFAPayloadDataType
} from '@models/requests/payloads'

import { LANGUAGE_MAP } from '@definitions/languages'

import { isAvailableLocalStorage } from '@scripts/helpers/windowHelpers'
import { prepareQrCodeParams } from '@scripts/entities/qrCode'

export const globalStore = defineStore('globalStore', {
  state: () => ({
    isAuthorized: false,
    isCheckedAuthorization: false,
    routeNameForAuthorizedStatusSwitching: null as null | string,
    isLoadingState: false,
    lang: {
      available: [] as LanguageEnum[],
      val: '' as LanguageEnum
    },
    user: null as UserType | null,
    userList: null as UserType[] | null,
    credentialList: [] as CredentialType[],
    pointList: [] as PointType[],
    transactionList: [] as TransactionType[],
    pagination: {
      transactionsNextCursor: 0
    } as Record<string, number | null>,
    emailForRestorePassword: '',
    mfaToken: '',
    mfaTokenExpiredError: false,
    isShownSlideScreen: false
  }),
  getters: {
    getUserCompany(): UserCompanyType | null {
      return this.user?.company ?? null
    },
    getUserLanguage(): LanguageEnum | null {
      return this.user?.lang ?? null
    },
    getUserMFAStatus(): boolean {
      return this.user?.login_mfa ?? false
    },
    getUserPermissions(): UserPermissionsType | null {
      return this.user?.user_permissions ?? null
    },
    isOwnerUser(): boolean {
      return !!this.getUserCompany
    },
    availableSalepoints(): SalePointPermissionsType[] {
      return this.getUserPermissions?.salepoints ?? []
    },
    availablePointList(): PointType[] {
      return this.isOwnerUser
        ? this.pointList
        : this.pointList
          .filter(point => this.availableSalepoints
            .some(salepoint => (salepoint.code === point.code) && salepoint.checked))
    },
    // TODO: Раскомментировать и применить после добавления логики создания счетов и выгрузки реестра
    // isAvailableCreatingInvoicesAndPayouts(): boolean {
    //   return this.isOwnerUser || (this.getUserPermissions?.permissions.creating_invoices_and_payouts ?? false)
    // },
    // isAvailableRegistryExport(): boolean {
    //   return this.isOwnerUser || (this.getUserPermissions?.permissions.registry_export ?? false)
    // },
    isAvailableManageApiSettings(): boolean {
      return this.isOwnerUser || (this.getUserPermissions?.permissions.manage_api_settings ?? false)
    },
    getCurrentLanguage(): LanguageEnum {
      return this.lang && (Object.keys(LANGUAGE_MAP) as LanguageEnum[])
        .some((languageKey: LanguageEnum) => languageKey === this.lang.val)
          ? this.lang.val
          : LanguageEnum.en
    },
    getAvailableLanguages(): LanguageEnum[] {
      return this.lang && this.lang.available ? this.lang.available : []
    },
    getCurrentLanguageShortName(): string {
      return LANGUAGE_MAP[this.getCurrentLanguage].shortName
    },
    getTransactionsNextCursor(): number | null {
      return this.pagination.transactionsNextCursor
    }
  },
  actions: {
    setAuthorizationStatus(status: boolean = true, routeName?: string | null) {
      this.routeNameForAuthorizedStatusSwitching = routeName || (status ? null : 'login')
      this.isAuthorized = status
    },
    setCheckedAuthorizationStatus(status: boolean = true) {
      this.isCheckedAuthorization = status
    },
    setLoadingState(status: boolean = true) {
      this.isLoadingState = status
    },
    setEmailForRestorePassword(email: string) {
      this.emailForRestorePassword = email
    },
    setMFAToken(mfaToken: string) {
      this.mfaToken = mfaToken

      if (isAvailableLocalStorage()) {
        if (!!mfaToken) {
          localStorage.setItem('mfa_token', mfaToken)
        } else if (localStorage.getItem('mfa_token')) {
          localStorage.removeItem('mfa_token')
        }
      }
    },
    setMFATokenExpiredError(status: boolean = true) {
      this.mfaTokenExpiredError = status
    },
    setShownSlideScreen(status: boolean = true) {
      this.isShownSlideScreen = status
    },
    setAvailableLanguages(languages: LanguageEnum[]) {
      this.lang.available = languages
    },
    setCurrentLanguage(language: LanguageEnum) {
      if (this.lang) {
        this.lang.val = language
      }
    },
    setTransactionsNextCursor(cursor: number | null) {
      this.pagination.transactionsNextCursor = cursor
    },
    async getLanguages() {
      await api.get('/personal-api/v1/langs/')
        .then((response) => {
          this.setAvailableLanguages(response.data)
        })
        .catch((error) => {
          new AxiosErrorHandler(error)
        })
    },
    async getQrCode(payloadData: GetQrCodePayloadDataType) {
      return await api.get(`/ea76265754e842c381d43ef0ebef409c${prepareQrCodeParams(payloadData)}`, {
        baseURL: 'https://qr.payopera.com',
        withCredentials: false
      })
    },
    async login(payloadData: LoginRequestPayloadDataType | LoginMFARequestPayloadDataType) {
      await api.post('/personal-api/v1/user/login/', payloadData)
        .then((response) => {
          if (response.data && response.data.mfa_token) {
            this.setMFAToken(response.data.mfa_token)
          } else {
            this.setAuthorizationStatus(true, 'points')
            this.setMFAToken('')
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isMFATokenExpiredError) {
            this.setMFATokenExpiredError()
            this.setMFAToken('')
          }

        })
    },
    async logOut(successRouteName: string = 'login') {
      await api.post('/personal-api/v1/user/logout/')
        .then(() => {
          this.setAuthorizationStatus(false, successRouteName)
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
    },
    async sendEmailForResetPassword(email: string) {
      return await api.post('/personal-api/v1/user/request-password-reset/', { email })
    },
    async resetPassword(token: string, newPassword: string) {
      return await api.post('/personal-api/v1/user/password-reset/', { token, new_password: newPassword })
    },
    async getCredentials() {
      await api.get('/personal-api/v1/credentials/')
        .then((response) => {
          if (response.data) {
            this.credentialList = response.data
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
        .finally(async() => {
          if (import.meta.env.MODE === 'development') {
            const { default: mocks } = await import('@/dev/mocks.ts')
            this.credentialList = mocks.credentialListMock
          }
        })
    },
    async addCredentials() {
      const cookies = new AppCookies()

      await api.post('/personal-api/v1/credentials/', {}, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
    },
    async deleteCredentials(credentialsId: number) {
      const cookies = new AppCookies()

      await api.delete(`/personal-api/v1/credentials/${credentialsId}/`, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
    },
    async getPoints(params: Record<string, string | string[] | number | boolean | undefined> = {}) {
      this.setLoadingState()

      await api.get('/personal-api/v1/points/', { params })
        .then((response) => {
          if (response.data) {
            this.pointList = response.data
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
        .finally(async() => {
          if (import.meta.env.MODE === 'development') {
            const { default: mocks } = await import('@/dev/mocks.ts')
            this.pointList = mocks.pointListMock
          }
        })

      this.setLoadingState(false)
    },
    async getTransactions(params: Record<string, string | string[] | number | boolean | undefined> = {}, isNeedLoading: boolean = true) {
      if (isNeedLoading) {
        this.setLoadingState()
      }

      await api.get('/personal-api/v1/transactions/', { params })
        .then((response) => {
          if (response.data) {
            this.transactionList = this.getTransactionsNextCursor === 0
              ? response.data.result
              : [...this.transactionList, ...response.data.result]
            this.setTransactionsNextCursor(response.data.next_cursor)
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
        .finally(async() => {
          if (import.meta.env.MODE === 'development') {
            const { default: mocks } = await import('@/dev/mocks.ts')
            this.transactionList = mocks.transactionListMock
          }
        })

      this.setLoadingState(false)
    },
    async resendCallback(transactionId: string) {
      return await api.post('personal-api/v1/transactions/resend-callback/', { transaction_id: transactionId })
    },
    async getUserInfo(isInitial: boolean = false) {
      this.setLoadingState()

      await api.get('/personal-api/v1/user/')
        .then(async(response) => {
          if (response.data) {
            this.user = response.data
          }

          if (isInitial) {
            this.setAuthorizationStatus(true)
            this.setCheckedAuthorizationStatus(true)
            this.setLoadingState(false)
          } else {
            await this.getUserList()
            this.setLoadingState(false)
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
          if (isInitial) {
            this.setCheckedAuthorizationStatus(true)
          }
          this.setLoadingState(false)
        })
        .finally(async() => {
          if (import.meta.env.MODE === 'development') {
            const { default: mocks } = await import('@/dev/mocks.ts')
            this.user = mocks.userMock
          }
        })
    },
    async postUserInfo(payloadData: PostUserInfoRequestPayloadDataType) {
      const cookies = new AppCookies()

      await api.post('/personal-api/v1/user/', payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
    },
    async getUserList() {
      await api.get('/personal-api/v1/users/')
        .then((response) => {
          if (response.data) {
            this.userList = response.data
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogOutError) {
            this.setAuthorizationStatus(false)
          }
        })
        .finally(async() => {
          if (import.meta.env.MODE === 'development') {
            const { default: mocks } = await import('@/dev/mocks.ts')
            this.userList = mocks.userListMock
          }
        })
    },
    async postUserPermissions(userId: number, payloadData: PostUserPermissionsRequestPayloadDataType) {
      const cookies = new AppCookies()

      return await api.post(`/personal-api/v1/users/permissions/${userId}/`, payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
    },
    async inviteUser(payloadData: InviteUserRequestPayloadDataType) {
      const cookies = new AppCookies()

      return await api.post('/personal-api/v1/users/add/', payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
    },
    async createInvoice(payloadData: CreateInvoicePayloadDataType) {
      return await api.post('/personal-api/v1/invoices/', payloadData)
    },
    async setupMFA(payloadData: SetupMFAPayloadDataType) {
      const cookies = new AppCookies()

      return await api.post('/personal-api/v1/user/mfa/setup/', payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
    },
    async verifyMFA(payloadData: VerifyMFAPayloadDataType) {
      const cookies = new AppCookies()

      return await api.post('/personal-api/v1/user/mfa/verify/', payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
    },
    async disableMFA(payloadData: DisableMFAPayloadDataType) {
      const cookies = new AppCookies()

      return await api.post('/personal-api/v1/user/mfa/disable/', payloadData, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
    }
  }
})
